import React from "react";

// Inline SVG illustrations for each tutorial step.
// Kept small and schematic — the goal is wayfinding, not photoreal UI.

function Frame({ children }) {
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-indigo-50 via-white to-zinc-50 p-6">
      {children}
    </div>
  );
}

function WorkspaceSilhouette({ highlight }) {
  const isRail = highlight === "rail";
  const isCenter = highlight === "center";
  const isDrawer = highlight === "drawer";
  return (
    <svg viewBox="0 0 360 220" className="w-full">
      <rect x="0" y="0" width="360" height="220" rx="12" fill="#FFFFFF" stroke="#E4E4E7" />
      <rect
        x="0"
        y="0"
        width="88"
        height="220"
        rx="12"
        fill={isRail ? "#EEF2FF" : "#FAFAFA"}
        stroke={isRail ? "#6366F1" : "#E4E4E7"}
        strokeWidth={isRail ? 2 : 1}
      />
      <rect x="12" y="14" width="64" height="10" rx="2" fill="#D4D4D8" />
      <rect x="12" y="38" width="40" height="6" rx="2" fill="#A1A1AA" />
      {[58, 72, 86, 100, 114].map((y, i) => (
        <rect key={y} x="12" y={y} width="64" height="8" rx="2" fill={i === 0 ? "#6366F1" : "#E4E4E7"} />
      ))}
      <rect x="12" y="136" width="40" height="6" rx="2" fill="#A1A1AA" />
      {[156, 170, 184].map((y) => (
        <rect key={y} x="12" y={y} width="64" height="8" rx="2" fill="#E4E4E7" />
      ))}
      <rect
        x="88"
        y="0"
        width="180"
        height="220"
        fill={isCenter ? "#EEF2FF" : "#FFFFFF"}
        stroke={isCenter ? "#6366F1" : "transparent"}
        strokeWidth={isCenter ? 2 : 0}
      />
      <rect x="98" y="16" width="100" height="8" rx="2" fill="#71717A" />
      <rect x="98" y="30" width="140" height="6" rx="2" fill="#D4D4D8" />
      {[54, 70, 86, 102, 118, 134, 150, 166, 182, 198].map((y, i) => (
        <g key={y}>
          <rect x="98" y={y} width="160" height="10" rx="2" fill={i % 2 ? "#FAFAFA" : "#F4F4F5"} />
          <circle cx="104" cy={y + 5} r="2" fill="#A1A1AA" />
          <rect x="112" y={y + 2} width="60" height="6" rx="2" fill="#52525B" />
          <rect x="178" y={y + 2} width="24" height="6" rx="2" fill="#6366F1" opacity="0.5" />
          <rect x="210" y={y + 2} width="40" height="6" rx="2" fill="#D4D4D8" />
        </g>
      ))}
      <rect
        x="268"
        y="0"
        width="92"
        height="220"
        rx="0"
        fill={isDrawer ? "#EEF2FF" : "#FFFFFF"}
        stroke={isDrawer ? "#6366F1" : "#E4E4E7"}
        strokeWidth={isDrawer ? 2 : 1}
      />
      <rect x="280" y="16" width="64" height="8" rx="2" fill="#52525B" />
      <rect x="280" y="30" width="48" height="6" rx="2" fill="#D4D4D8" />
      <rect x="280" y="50" width="68" height="24" rx="4" fill="#F4F4F5" />
      <rect x="280" y="84" width="68" height="40" rx="4" fill="#F4F4F5" />
      <rect x="280" y="134" width="68" height="28" rx="4" fill="#F4F4F5" />
      <rect x="280" y="172" width="68" height="32" rx="4" fill="#E0E7FF" />
    </svg>
  );
}

function GmailBanner() {
  return (
    <svg viewBox="0 0 360 220" className="w-full">
      <rect x="0" y="0" width="360" height="220" rx="12" fill="#FFFFFF" stroke="#E4E4E7" />
      <rect x="16" y="20" width="150" height="10" rx="3" fill="#52525B" />
      <rect x="16" y="36" width="100" height="6" rx="2" fill="#A1A1AA" />
      <rect x="16" y="62" width="328" height="56" rx="10" fill="#FEF3C7" stroke="#FCD34D" />
      <rect x="28" y="74" width="180" height="8" rx="2" fill="#92400E" />
      <rect x="28" y="88" width="260" height="6" rx="2" fill="#B45309" />
      <rect x="28" y="100" width="240" height="6" rx="2" fill="#B45309" />
      <rect x="16" y="134" width="328" height="70" rx="10" fill="#F4F4F5" />
      <g transform="translate(32,150)">
        <rect width="30" height="22" rx="5" fill="#FFFFFF" stroke="#D4D4D8" />
        <path d="M4 5 L15 14 L26 5" stroke="#EF4444" strokeWidth="2" fill="none" />
      </g>
      <rect x="72" y="152" width="120" height="8" rx="2" fill="#52525B" />
      <rect x="72" y="166" width="180" height="6" rx="2" fill="#A1A1AA" />
      <rect x="72" y="178" width="150" height="6" rx="2" fill="#A1A1AA" />
      <rect x="264" y="156" width="68" height="24" rx="6" fill="#4F46E5" />
      <rect x="278" y="164" width="40" height="8" rx="2" fill="#FFFFFF" />
    </svg>
  );
}

function CreateCampaignCard() {
  return (
    <svg viewBox="0 0 360 220" className="w-full">
      <rect x="0" y="0" width="360" height="220" rx="12" fill="#FFFFFF" stroke="#E4E4E7" />
      <rect x="0" y="0" width="108" height="220" rx="12" fill="#FAFAFA" />
      <rect x="12" y="14" width="80" height="10" rx="2" fill="#A1A1AA" />
      <rect x="12" y="34" width="40" height="6" rx="2" fill="#71717A" />
      <rect x="64" y="34" width="34" height="6" rx="2" fill="#6366F1" />
      <rect x="12" y="54" width="84" height="100" rx="10" fill="#EEF2FF" stroke="#C7D2FE" />
      <rect x="22" y="66" width="64" height="8" rx="2" fill="#FFFFFF" stroke="#C7D2FE" />
      <rect x="22" y="82" width="64" height="20" rx="2" fill="#FFFFFF" stroke="#C7D2FE" />
      <rect x="22" y="110" width="64" height="18" rx="4" fill="#4F46E5" />
      <rect x="36" y="116" width="36" height="6" rx="2" fill="#FFFFFF" />
      <rect x="120" y="18" width="220" height="10" rx="3" fill="#52525B" />
      <rect x="120" y="34" width="150" height="6" rx="2" fill="#A1A1AA" />
      <rect x="120" y="60" width="220" height="80" rx="10" fill="#F4F4F5" />
      <rect x="136" y="74" width="80" height="8" rx="2" fill="#71717A" />
      <rect x="136" y="92" width="188" height="6" rx="2" fill="#D4D4D8" />
      <rect x="136" y="104" width="164" height="6" rx="2" fill="#D4D4D8" />
      <rect x="136" y="116" width="120" height="6" rx="2" fill="#D4D4D8" />
      <rect x="120" y="154" width="104" height="46" rx="10" fill="#ECFDF5" stroke="#A7F3D0" />
      <rect x="130" y="164" width="60" height="6" rx="2" fill="#065F46" />
      <rect x="130" y="176" width="84" height="6" rx="2" fill="#047857" />
      <rect x="130" y="186" width="60" height="6" rx="2" fill="#047857" />
      <rect x="236" y="154" width="104" height="46" rx="10" fill="#F4F4F5" />
      <rect x="246" y="164" width="60" height="6" rx="2" fill="#52525B" />
      <rect x="246" y="176" width="84" height="6" rx="2" fill="#A1A1AA" />
      <rect x="246" y="186" width="60" height="6" rx="2" fill="#A1A1AA" />
    </svg>
  );
}

function SequenceTimeline() {
  const steps = [
    { x: 40, label: "Intro", day: "Day 0", time: "9:00 AM" },
    { x: 140, label: "Follow up 1", day: "+3 business days", time: "10:30 AM" },
    { x: 240, label: "Follow up 2", day: "+7 business days", time: "9:30 AM" },
    { x: 324, label: "Close", day: "+14 days", time: "9:00 AM" },
  ];
  return (
    <svg viewBox="0 0 360 220" className="w-full">
      <rect x="0" y="0" width="360" height="220" rx="12" fill="#FFFFFF" stroke="#E4E4E7" />
      <rect x="16" y="16" width="180" height="10" rx="3" fill="#52525B" />
      <rect x="16" y="32" width="260" height="6" rx="2" fill="#A1A1AA" />
      <rect x="16" y="46" width="120" height="14" rx="7" fill="#ECFDF5" stroke="#A7F3D0" />
      <text x="76" y="56" textAnchor="middle" fontSize="9" fontWeight="600" fill="#065F46">
        Skip weekends · ON
      </text>
      <line x1="24" y1="110" x2="340" y2="110" stroke="#E4E4E7" strokeWidth="2" strokeDasharray="4 4" />
      {steps.map((step, i) => (
        <g key={step.label}>
          <circle cx={step.x} cy={110} r="10" fill={i < 3 ? "#4F46E5" : "#A1A1AA"} />
          <text x={step.x} y={114} textAnchor="middle" fontSize="10" fill="#FFFFFF" fontWeight="700">
            {i + 1}
          </text>
          <rect x={step.x - 36} y={126} width="72" height="12" rx="3" fill="#F4F4F5" />
          <text x={step.x} y={136} textAnchor="middle" fontSize="9" fill="#52525B">
            {step.label}
          </text>
          <text x={step.x} y={90} textAnchor="middle" fontSize="9" fill="#A1A1AA">
            {step.day}
          </text>
          <rect x={step.x - 24} y={144} width="48" height="12" rx="6" fill="#EEF2FF" />
          <text x={step.x} y={153} textAnchor="middle" fontSize="8" fontWeight="600" fill="#4338CA">
            {step.time}
          </text>
        </g>
      ))}
      <rect x="16" y="170" width="328" height="36" rx="10" fill="#FAFAFA" stroke="#E4E4E7" />
      <rect x="28" y="180" width="140" height="6" rx="2" fill="#52525B" />
      <rect x="28" y="192" width="220" height="6" rx="2" fill="#A1A1AA" />
      <rect x="278" y="178" width="52" height="18" rx="6" fill="#4F46E5" />
      <rect x="292" y="184" width="24" height="6" rx="2" fill="#FFFFFF" />
    </svg>
  );
}

function CsvFunnel() {
  const pills = ["Upload", "Columns", "Mapping", "Validate", "Review"];
  return (
    <svg viewBox="0 0 360 220" className="w-full">
      <rect x="0" y="0" width="360" height="220" rx="12" fill="#FFFFFF" stroke="#E4E4E7" />
      <g>
        {pills.map((label, i) => {
          const x = 12 + i * 68;
          const isActive = i === 2;
          return (
            <g key={label}>
              <rect x={x} y={20} width={64} height={22} rx={11} fill={isActive ? "#18181B" : i < 2 ? "#ECFDF5" : "#F4F4F5"} />
              <text x={x + 32} y={35} textAnchor="middle" fontSize="10" fontWeight="600" fill={isActive ? "#FFFFFF" : i < 2 ? "#065F46" : "#52525B"}>
                {label}
              </text>
              {i < pills.length - 1 ? (
                <line x1={x + 64} y1={31} x2={x + 68} y2={31} stroke="#D4D4D8" />
              ) : null}
            </g>
          );
        })}
      </g>
      <rect x="16" y="60" width="216" height="144" rx="10" fill="#FFFFFF" stroke="#E4E4E7" />
      {[
        { label: "Email", mapped: "email_address", tone: "#ECFDF5", ring: "#A7F3D0" },
        { label: "Company", mapped: "company", tone: "#ECFDF5", ring: "#A7F3D0" },
        { label: "Website", mapped: "url", tone: "#ECFDF5", ring: "#A7F3D0" },
        { label: "Owner", mapped: "Unmapped", tone: "#F4F4F5", ring: "#D4D4D8" },
        { label: "Phone", mapped: "Unmapped", tone: "#F4F4F5", ring: "#D4D4D8" },
      ].map((row, i) => (
        <g key={row.label}>
          <rect x="26" y={72 + i * 24} width="196" height="20" rx="6" fill={row.tone} stroke={row.ring} />
          <text x="36" y={86 + i * 24} fontSize="10" fontWeight="600" fill="#18181B">
            {row.label}
          </text>
          <text x="200" y={86 + i * 24} fontSize="10" textAnchor="end" fill="#3F3F46">
            {row.mapped}
          </text>
        </g>
      ))}
      <rect x="244" y="60" width="100" height="144" rx="10" fill="#FAFAFA" stroke="#E4E4E7" />
      <rect x="256" y="72" width="60" height="8" rx="2" fill="#52525B" />
      {[
        { label: "New", value: "312", tone: "#065F46" },
        { label: "Updates", value: "47", tone: "#3730A3" },
        { label: "Skipped", value: "3", tone: "#92400E" },
        { label: "Errors", value: "1", tone: "#991B1B" },
      ].map((row, i) => (
        <g key={row.label}>
          <text x="256" y={98 + i * 24} fontSize="10" fill="#71717A">
            {row.label}
          </text>
          <text x="332" y={98 + i * 24} fontSize="12" fontWeight="700" textAnchor="end" fill={row.tone}>
            {row.value}
          </text>
        </g>
      ))}
    </svg>
  );
}

function SmartViewsGrid() {
  const views = [
    { key: "1", label: "Today", tone: "#EEF2FF", ring: "#C7D2FE", accent: "#4F46E5" },
    { key: "2", label: "Replies", tone: "#ECFDF5", ring: "#A7F3D0", accent: "#059669" },
    { key: "3", label: "Follow-ups", tone: "#FEF3C7", ring: "#FCD34D", accent: "#B45309" },
    { key: "4", label: "Queue", tone: "#DBEAFE", ring: "#93C5FD", accent: "#1D4ED8" },
    { key: "5", label: "All leads", tone: "#F4F4F5", ring: "#D4D4D8", accent: "#3F3F46" },
  ];
  return (
    <svg viewBox="0 0 360 220" className="w-full">
      <rect x="0" y="0" width="360" height="220" rx="12" fill="#FFFFFF" stroke="#E4E4E7" />
      <rect x="16" y="16" width="140" height="10" rx="3" fill="#52525B" />
      <rect x="16" y="32" width="260" height="6" rx="2" fill="#A1A1AA" />
      {views.map((view, i) => (
        <g key={view.key} transform={`translate(${16 + i * 68}, 60)`}>
          <rect width="60" height="140" rx="10" fill={view.tone} stroke={view.ring} />
          <rect x="10" y="12" width="22" height="22" rx="6" fill="#FFFFFF" stroke={view.ring} />
          <text x="21" y="28" fontSize="12" fontWeight="700" textAnchor="middle" fill={view.accent}>
            {view.key}
          </text>
          <rect x="10" y="46" width="40" height="8" rx="2" fill={view.accent} />
          <rect x="10" y="62" width="34" height="6" rx="2" fill="#A1A1AA" />
          <rect x="10" y="74" width="40" height="6" rx="2" fill="#A1A1AA" />
          <rect x="10" y="86" width="26" height="6" rx="2" fill="#A1A1AA" />
          <circle cx="20" cy="112" r="3" fill={view.accent} />
          <rect x="28" y="109" width="20" height="6" rx="2" fill={view.accent} opacity="0.6" />
          <circle cx="20" cy="124" r="3" fill={view.accent} opacity="0.6" />
          <rect x="28" y="121" width="24" height="6" rx="2" fill={view.accent} opacity="0.4" />
        </g>
      ))}
    </svg>
  );
}

function DrawerHotkeys() {
  return (
    <svg viewBox="0 0 360 220" className="w-full">
      <rect x="0" y="0" width="360" height="220" rx="12" fill="#FFFFFF" stroke="#E4E4E7" />
      <rect x="0" y="0" width="168" height="220" rx="12" fill="#FAFAFA" />
      {[40, 64, 88, 112, 136, 160, 184].map((y, i) => (
        <g key={y}>
          <rect x="12" y={y} width="144" height="14" rx="3" fill={i === 2 ? "#EEF2FF" : "#FFFFFF"} stroke={i === 2 ? "#6366F1" : "#E4E4E7"} />
          <rect x="20" y={y + 3} width="80" height="6" rx="2" fill="#52525B" />
          <rect x="108" y={y + 3} width="36" height="6" rx="2" fill="#A1A1AA" />
        </g>
      ))}
      <rect x="168" y="0" width="192" height="220" rx="0" fill="#FFFFFF" stroke="#6366F1" strokeWidth="2" />
      <rect x="180" y="16" width="100" height="10" rx="3" fill="#18181B" />
      <rect x="180" y="32" width="80" height="6" rx="2" fill="#71717A" />
      <g transform="translate(180, 52)">
        <rect width="168" height="34" rx="8" fill="#F4F4F5" />
        <rect x="10" y="10" width="60" height="6" rx="2" fill="#52525B" />
        <rect x="10" y="22" width="120" height="6" rx="2" fill="#A1A1AA" />
      </g>
      <g transform="translate(180, 94)">
        <rect width="168" height="56" rx="8" fill="#F4F4F5" />
        <rect x="10" y="10" width="80" height="6" rx="2" fill="#52525B" />
        <rect x="10" y="24" width="140" height="6" rx="2" fill="#A1A1AA" />
        <rect x="10" y="36" width="120" height="6" rx="2" fill="#A1A1AA" />
      </g>
      <g transform="translate(180, 158)">
        <rect width="168" height="46" rx="8" fill="#E0E7FF" />
        <rect x="10" y="10" width="60" height="6" rx="2" fill="#3730A3" />
        <rect x="10" y="22" width="100" height="18" rx="6" fill="#FFFFFF" stroke="#C7D2FE" />
      </g>
      <g transform="translate(12, 196)">
        <rect width="28" height="14" rx="3" fill="#FFFFFF" stroke="#D4D4D8" />
        <text x="14" y="11" textAnchor="middle" fontSize="9" fontWeight="700" fill="#18181B">
          j / k
        </text>
      </g>
    </svg>
  );
}

const VISUALS = {
  welcome: WorkspaceSilhouette,
  rail: WorkspaceSilhouette,
  gmail: GmailBanner,
  campaign: CreateCampaignCard,
  sequence: SequenceTimeline,
  csv: CsvFunnel,
  smartViews: SmartViewsGrid,
  drawer: DrawerHotkeys,
};

export default function StepVisual({ variant = "welcome", highlight }) {
  const Component = VISUALS[variant] || WorkspaceSilhouette;
  return (
    <Frame>
      <Component highlight={highlight} />
    </Frame>
  );
}
